<script setup lang="ts">
import ModalComponent from "@/components/ModalComponent.vue";
import { defineProps, defineEmits, ref } from "vue";
import { useI18n } from "vue-i18n";
import GradientButton from "@/components/buttons/GradientButton.vue";
import { useRouter } from "vue-router";
import { ROUTE_NAMES } from "@/constants/routeNames";
import { PREMIUM_CALCULATORS } from "@/types/premiumTypes";
import { fetchStripeDestinyMatrixProductUrl } from "@/api/subscription";
import { useUserStore } from "@/stores/userStore";

const props = defineProps<{
  show: boolean;
  selectedDate: string;
}>();

const emits = defineEmits(["close"]);

const { t } = useI18n();
const router = useRouter();
const authStore = useUserStore();

const waitApiCall = ref(false);

function openSubscribePage() {
  const query = { openCalculator: PREMIUM_CALCULATORS.destinyMatrix };

  router.push({ name: ROUTE_NAMES.USER, query: query });
}

async function openStripePurchasePage() {
  if (authStore.user === null) {
    return router.push({ name: ROUTE_NAMES.USER });
  }
  try {
    waitApiCall.value = true;
    const apiDate = props.selectedDate.split(".").reverse().join("-");
    const stripeUrl = await fetchStripeDestinyMatrixProductUrl(apiDate);

    if (stripeUrl) {
      window.location.href = stripeUrl;
    }
  } catch (e) {
    console.error(e);
  } finally {
    waitApiCall.value = false;
  }
}
</script>

<template>
  <ModalComponent
    :show="show"
    @close="emits('close')"
    containerClass="bg-white !max-w-md"
  >
    <template #header>
      <p class="font-semibold text-2xl text-black sf-pro">
        {{ t("calculators.destiny-matrix.premium-modal.title") }}
      </p>
    </template>
    <template #content>
      <div class="flex flex-col gap-2 sf-pro">
        <p
          class="text-lg font-medium leading-[20px] text-brown whitespace-pre-line mb-2"
        >
          {{
            t("calculators.destiny-matrix.premium-modal.body", {
              date: selectedDate,
            })
          }}
        </p>

        <GradientButton
          class="h-[46px] md:h-14"
          bg-color="bg-brown-gradient"
          hover-bg-color="bg-orange-gradient"
          :text="
            t('calculators.destiny-matrix.premium-modal.buy-subscribe-btn')
          "
          @click="openSubscribePage"
        />

        <GradientButton
          class="h-[46px] md:h-14"
          bg-color="bg-brown-gradient"
          hover-bg-color="bg-orange-gradient"
          :text="
            t('calculators.destiny-matrix.premium-modal.buy-date-btn', {
              date: selectedDate,
            })
          "
          :isDisabled="waitApiCall"
          :isLoading="waitApiCall"
          @click="openStripePurchasePage"
        />
      </div>
    </template>
  </ModalComponent>
</template>
